import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CustomQuestionService } from './custom-question.service';
import {
  debounceTime,
  finalize,
  Observable,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { LoaderService } from '../../../core/services/loader.service';
import { CommonService } from '../../../shared/services/common.service';
import { NzTableQueryParams, NzTableModule } from 'ng-zorro-antd/table';
import { ErrorResponse } from '../../../core/models/error.models';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { LookupItem } from '../../../core/models/lookup.models';
import {
  ApiResponses,
  BipTemplateListModel,
} from '../../insights/insights.interface';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { QuestionItem } from '../manage-settings.interface';
import { OrganizationDetailModel } from '../../organizations/organization.interface';
import { AuthService } from '../../../core/services/auth.service';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { InsightsService } from '../../insights/insights.service';
import _ from 'lodash';
import { ActivatedRoute } from '@angular/router';
import { OrganizationSelectionService } from '../../../core/services/organization-selection.service';

@Component({
  selector: 'app-custom-question',
  standalone: true,
  imports: [
    NzTableModule,
    CommonModule,
    SvgIconComponent,
    NzButtonModule,
    NzSpinModule,
    ReactiveFormsModule,
    NzFormControlComponent,
    NzFormItemComponent,
    NzFormModule,
    NzInputModule,
    NzCheckboxModule,
    NzSelectModule,
    DragDropModule,
    CdkDropList,
    CdkDrag,
    NzDropDownModule,
  ],
  templateUrl: './custom-question.component.html',
  styleUrl: './custom-question.component.scss',
})
export class CustomQuestionComponent implements OnDestroy, OnInit {
  private customService = inject(CustomQuestionService);
  pageSize = 100;
  pageIndex = 1;
  total = 0;
  sort = 'sequenceNumber';
  sortOrder: number | undefined = 0;
  searchTerm = '';
  errorMessage = '';
  private readonly destroy$ = new Subject<void>();
  totalRows = 0;
  customTime$ = this.customService.customQuestions$;
  loading$: Observable<boolean>;
  reportTypes: LookupItem[] = [];
  isQuestionForm = false;
  customQuestionForm!: FormGroup;
  isEditMode = false;
  editCustomQuestionForm!: FormGroup;
  selectedRecord: QuestionItem = {} as QuestionItem;
  questionList: QuestionItem[] = [];
  orgId: number | null = 0;
  orgData: OrganizationDetailModel | null = null;
  sequenceNumbers: number[] = [];
  bipTemplateList: BipTemplateListModel[] = [];
  listOfColumn = [
    {
      title: 'Question',
      compare: null,
    },
    {
      title: 'Is Required',
      compare: null,
    },
    {
      title: 'Is Active',
      compare: null,
    },
    {
      title: 'Report Type',
      compare: null,
    },
    {
      title: 'Template',
      compare: null,
    },
    { title: '', compare: null },
  ];
  constructor(
    private loaderService: LoaderService,
    private cd: ChangeDetectorRef,
    private commonService: CommonService,
    private fb: FormBuilder,
    private authService: AuthService,
    private insightsService: InsightsService,
    private route: ActivatedRoute,
    private orgSelectionService: OrganizationSelectionService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.orgSelectionService.orgId$
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe(res => {
        this.orgId = res || 0;
        this.getBipTemplates(this.orgId);
        this.getMasterData();
        this.loadData();
      });
    this.customQuestionForm = this.fb.group({
      id: [0],
      customQuestion: ['', [Validators.required, Validators.maxLength(500)]],
      isRequired: [false],
      statusId: [1],
      bipTemplates: [[]],
      questionForReportTypeId: ['', Validators.required],
    });
    this.editCustomQuestionForm = this.fb.group({
      id: [0],
      editIndex: [-1],
      parentOrganizationId: [0],
      customQuestion: ['', [Validators.required, Validators.maxLength(500)]],
      isRequired: [false],
      statusId: [1],
      bipTemplates: [[]],
      questionForReportTypeId: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.customTime$.subscribe(response => {
      if (response && response.result) {
        this.questionList = response.result.items;
        this.totalRows = response.result.totalRows;
        this.sequenceNumbers = this.questionList
          .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
          .map(q => q.sequenceNumber);
      }
    });
  }
  get getFormGroup(): FormGroup {
    return this.isEditMode
      ? this.editCustomQuestionForm
      : this.customQuestionForm;
  }
  onQueryParamsChange(params: NzTableQueryParams) {
    this.pageSize = params.pageSize;
    this.pageIndex = params.pageIndex;
    const currentSort = params.sort.find(item => item.value !== null);
    this.sort = (currentSort && currentSort.key) || 'sequenceNumber';
    this.sortOrder =
      !!currentSort && currentSort.value
        ? currentSort.value === 'ascend'
          ? 0
          : 1
        : 0;
    this.loadData();
  }

  getBipTemplates(orgId: number) {
    if (orgId === null || orgId === undefined) {
      this.commonService.notify('error', 'Organization ID not found');
      return;
    }

    this.loaderService.show();
    this.insightsService
      .getBipTemplates(orgId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ApiResponses<BipTemplateListModel>) => {
          this.bipTemplateList = res.result;
          this.cd.detectChanges();
        },
        error: (error: Error) => {
          this.loaderService.hide();
          this.commonService.notify('error', error.message);
        },
      });
  }

  async getMasterData() {
    await Promise.all([
      this.commonService.getDataByType('QuestionForReportType').toPromise(),
    ])
      .then(([reportList]: [ApiResponses<LookupItem> | undefined]) => {
        this.reportTypes = reportList ? reportList.result : [];
      })
      .catch(e => console.error(e))
      .finally(() => this.loaderService.hide());
  }

  loadData(): void {
    if (this.orgId === null || this.orgId === undefined) {
      return;
    }

    this.loaderService.show();
    this.customService
      .getCustomQuestions({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        sortOrder: this.sortOrder,
        sort: this.sort,
        parentOrganizationId: this.orgId,
      })
      .pipe(
        take(1),
        finalize(() => this.loaderService.hide()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.cd.detectChanges();
        },
        error: (error: ErrorResponse) => {
          this.loaderService.hide();
          this.commonService.notify('error', error.message);
        },
      });
  }

  onCheckboxChange(ctrl: string, checked: boolean, value: number): void {
    this.getFormGroup.get(ctrl)?.markAsTouched();
    const selectedOptions = this.getFormGroup.get(ctrl)?.value || [];
    if (checked) {
      selectedOptions.push(value);
    } else {
      const index = selectedOptions.indexOf(value);
      if (index > -1) {
        selectedOptions.splice(index, 1);
      }
    }
    this.getFormGroup.get(ctrl)?.setValue(selectedOptions);
  }

  getBipTemplateLabel(bipTemplates: number[]) {
    const indexes = ['1'];

    const matchedIndexes = _.chain(this.bipTemplateList)
      .map((item: BipTemplateListModel, index) =>
        bipTemplates.includes(item.id) ? index + 1 : null
      )
      .compact()
      .value();

    return _.join(_.uniq([...indexes, ...matchedIndexes]), ', ');
  }

  editQuestion(index: number) {
    this.isEditMode = true;
    this.isQuestionForm = false;
    this.selectedRecord = this.questionList[index];
    this.editCustomQuestionForm.patchValue({
      id: this.selectedRecord.id,
      editIndex: index,
      parentOrganizationId: this.selectedRecord.parentOrganizationId,
      customQuestion: this.selectedRecord.customQuestion,
      isRequired: this.selectedRecord.isRequired,
      statusId: this.selectedRecord.statusId,
      questionForReportTypeId: this.selectedRecord.questionForReportTypeId,
      bipTemplates: this.selectedRecord.bipTemplates,
    });
  }

  onSubmit() {
    if (this.orgId === null || this.orgId === undefined) {
      this.commonService.notify('error', 'Organization ID not found');
      return;
    }

    this.loaderService.show();
    const operation$ = this.isEditMode
      ? this.customService.updateQuestion(
          this.editCustomQuestionForm.value,
          this.orgId
        )
      : this.customService.createQuestion(
          this.customQuestionForm.value,
          this.orgId
        );
    operation$.pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.loadData();
        this.isEditMode = false;
        this.selectedRecord = {} as QuestionItem;
        this.customQuestionForm.reset();
        this.editCustomQuestionForm.reset();
        this.isQuestionForm = false;
        this.commonService.notify(
          'success',
          `Question ${this.isEditMode ? 'updated' : 'created'} successfully`
        );
        this.cd.detectChanges();
      },
      error: error => {
        this.loaderService.hide();
        this.handleError(error);
      },
    });
  }

  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
    this.cd.detectChanges();
  }
  onCancel() {
    this.isQuestionForm = false;
    this.isEditMode = false;
    this.customQuestionForm.reset();
    this.editCustomQuestionForm.reset();
  }

  addQuestion() {
    this.isQuestionForm = true;
    this.isEditMode = false;
    this.editCustomQuestionForm.reset();
  }

  drop(event: CdkDragDrop<QuestionItem[]>): void {
    const currentArray = event.container.data;
    moveItemInArray(this.questionList, event.previousIndex, event.currentIndex);
    const newArray = currentArray.map(({ id }, index) => ({
      id,
      sequenceNumber: this.sequenceNumbers[index],
    }));
    this.updateSequencenumber(newArray);
  }

  updateSequencenumber(dataArr: { id: number; sequenceNumber: number }[]) {
    const payload = {
      questionSequenceNumbers: dataArr,
    };
    this.loaderService.show();
    const operation$ = this.customService.updateSequenceNumber(payload);
    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.cd.detectChanges();
          this.commonService.notify(
            'success',
            `Question sequence successfully Updated`
          );
          this.loadData();
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
